import React, { useEffect, useState } from 'react'
import { apiClient } from 'pobble-components/dist/api'
import injectSheet from 'react-jss'
import { errorHandler } from '../../error-handler'
import { PobbleAvatar } from 'pobble-components/dist/pobble-avatar'
import Button from 'pobble-components/dist/button'
import FormTextArea from 'pobble-components/dist/form-elements/form-text-area'
import ButtonElement from 'pobble-components/dist/button-element'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { WORK_HIGHLIGHT_SETTINGS } from 'pobble-components/dist/work/highlighter'

const apiAddReview = (workVersionId, data, params) =>
  apiClient.post(
    `/v3/pa_work_versions/${workVersionId}/pa_reviews/annotate`,
    data,
    { params }
  )

const apiAddReviewComment = (id, data, params) =>
  apiClient.post(`/v3/pa_reviews/${id}/add_comment`, data, { params })

export const useWorkReviews = action => {
  const [reviews, setReviews] = useState(null)
  const workVersion = action && action.last_pa_work_version

  useEffect(() => {
    if (action) {
      setReviews(action.pa_reviews)
    }
  }, [action])

  const addReview = ({ comment, color }) => {
    apiAddReview(
      workVersion.id,
      {
        body: comment,
        colour: color
      },
      {
        include:
          'pa_review.type,pa_review.pa_review_comments,pa_review.pa_review_comment.content,pa_review.pa_review_comment.author.names,pa_review.pa_review_comment.author.avatar'
      }
    )
      .then(res => {
        setReviews([...reviews, res.data.pa_review])
      })
      .catch(errorHandler)
  }

  const addComment = (reviewId, value) => {
    apiAddReviewComment(
      reviewId,
      {
        body: value,
        pa_work_version_id: workVersion.id
      },
      {
        include:
          'pa_review_comment.content,pa_review_comment.author.names,pa_review_comment.author.avatar'
      }
    )
      .then(res => {
        setReviews(
          reviews.map(review => {
            if (review.id !== reviewId) {
              return review
            } else {
              return {
                ...review,
                pa_review_comments: [
                  ...review.pa_review_comments,
                  res.data.pa_review_comment
                ]
              }
            }
          })
        )
      })
      .catch(errorHandler)
  }

  return {
    reviews,
    updateData: setReviews,
    addReview,
    addComment
  }
}

const reviewsFormStyles = {
  footer: {
    marginTop: '16px',
    display: 'flex',
    '& > button': {
      marginLeft: 'auto'
    }
  },
  color: {
    background: '#f0f0f0',
    padding: '10px',
    borderRadius: '8px 8px 0 0',
    display: 'flex'
  },
  textArea: {
    borderRadius: '0 0 8px 8px'
  },
  colorButton: {
    position: 'relative',
    width: '32px',
    height: '32px',
    borderRadius: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '8px',
    background: '#fff',
    border: '1px solid #e6e6e6'
  },
  colorIndicator: {
    width: '24px',
    height: '24px',
    borderRadius: '24px',
    display: 'block'
  },
  colorCheck: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: '#fff'
  }
}

const ReviewsForm = injectSheet(reviewsFormStyles)(
  ({ classes, onSubmit, green }) => {
    const [value, setValue] = useState('')
    const [pending, setPending] = useState(false)
    const [selectedColor, setSelectedColor] = useState(null)
    return (
      <form
        onSubmit={e => {
          e.preventDefault()
          setPending(true)
          onSubmit({
            comment: value,
            color: selectedColor
          })
        }}>
        <div>
          <div>
            <div className={classes.color}>
              {[null, ...WORK_HIGHLIGHT_SETTINGS.penColors].map(color => {
                return (
                  <ButtonElement
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={classes.colorButton}>
                    <span
                      className={classes.colorIndicator}
                      style={{ backgroundColor: color }}
                    />
                    {color === selectedColor && (
                      <span className={classes.colorCheck}>
                        <FontAwesomeIcon
                          style={{ color: !color && '#666' }}
                          icon={faCheck}
                        />
                      </span>
                    )}
                  </ButtonElement>
                )
              })}
            </div>
            <FormTextArea
              className={classes.textArea}
              required
              value={value}
              placeholder="Type your comment here"
              onChange={e => setValue(e.target.value)}
            />
          </div>
        </div>
        <div className={classes.footer}>
          <Button pending={pending} type="submit" tall secondary green={green}>
            Add your comment
          </Button>
        </div>
      </form>
    )
  }
)

const commentFormStyles = {
  container: {
    margin: '16px 0 12px'
  },
  footer: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
    '& > *': {
      marginLeft: '4px'
    }
  }
}

const CommentForm = injectSheet(commentFormStyles)(
  ({ classes, onSubmit, onCancel, green }) => {
    const [value, setValue] = useState('')
    const [pending, setPending] = useState(false)
    return (
      <form
        className={classes.container}
        onSubmit={e => {
          e.preventDefault()
          setPending(true)
          onSubmit(value)
        }}>
        <FormTextArea
          required
          value={value}
          placeholder="Type your comment here"
          onChange={e => setValue(e.target.value)}
        />
        <div className={classes.footer}>
          <Button tall simple green={green} action={onCancel}>
            Cancel
          </Button>
          <Button pending={pending} type="submit" tall green={green}>
            Add comment
          </Button>
        </div>
      </form>
    )
  }
)

const reviewsStyle = ({ FONT_SIZES }) => ({
  review: {
    background: '#fff',
    borderRadius: '8px',
    padding: '16px 16px 8px',
    marginBottom: '16px',
    boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    overflow: 'hidden'
  },
  comment: {
    display: 'flex',
    ...FONT_SIZES.BODY_DEFAULT.STYLE,
    paddingBottom: '16px',
    borderBottom: '1px solid #ececec',
    marginTop: '16px',
    '&:first-child': {
      marginTop: '0'
    }
  },
  avatar: {
    fontSize: '40px',
    marginRight: '8px',
    border: '1px solid #f0f0f0',
    flexShrink: '0'
  },
  name: {
    display: 'block',
    marginBottom: '4px'
  },
  reviewFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '8px'
  },
  colorBadge: {
    width: '40px',
    height: '40px',
    transform: 'rotate(45deg)',
    position: 'absolute',
    right: '-20px',
    top: '-20px'
  }
})

export const WorkReviews = injectSheet(reviewsStyle)(
  ({ classes, reviews, addReview, addComment, green }) => {
    const [openReplyForm, setOpenReplyForm] = useState(null)

    useEffect(() => {
      setOpenReplyForm(null)
    }, [reviews])

    return (
      <div>
        {reviews.map((review, index) => {
          return (
            <div key={index} className={classes.review}>
              {review.pa_review_comments.map(comment => {
                return (
                  <div
                    key={comment.author.full_name}
                    className={classes.comment}>
                    <PobbleAvatar
                      name={comment.author.full_name}
                      className={classes.avatar}
                      avatar={comment.author.avatar}
                    />
                    <div>
                      <strong className={classes.name}>
                        {comment.author.full_name}
                      </strong>
                      {comment.body}
                    </div>
                  </div>
                )
              })}
              {review.colour && (
                <div
                  className={classes.colorBadge}
                  style={{ backgroundColor: review.colour }}
                />
              )}
              {openReplyForm !== review.id && (
                <div className={classes.reviewFooter}>
                  <Button
                    tall
                    simple
                    green={green}
                    action={() => setOpenReplyForm(review.id)}>
                    Reply
                  </Button>
                </div>
              )}
              {openReplyForm === review.id && (
                <CommentForm
                  green={green}
                  onSubmit={value => addComment(review.id, value)}
                  onCancel={() => setOpenReplyForm(null)}
                />
              )}
            </div>
          )
        })}
        <ReviewsForm green={green} key={reviews} onSubmit={addReview} />
      </div>
    )
  }
)
